import { canv } from "@/canv/global.js";
import {
    drawRoundedRect,
    easeInSine,
    getBoolProperty,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils.js";
import { saveTextToClipboard } from "@/canvasController/helpers";
import { elementBase } from "@/elementBase/elementBase.js";
import { TableWidgetInterface } from "@/elements/table/interface";
import { TableWidgetCell } from "@/elements/table/tableCell";
import { TableWidgetRow } from "@/elements/table/tableRow.js";
import { textLine } from "@/elements/text/textLine.js";
import { selectionManager } from "@/tools/cursorTool";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import type {
    RowColumnResizeData,
    TableCellsSelectingData,
    TableCoordinates,
    TableOverAnyCell,
    TableSearchTextResult,
    TableSelected,
    TableWidgetCellStateMap,
    TableWidgetRowStateMap,
} from "./types";

const MIN_CELL_WIDTH = 70;

export class TableWidget extends elementBase {
    constructor(startX = 0, startY = 0) {
        super();

        this.interface = new TableWidgetInterface(this);
        this.startX = startX;
        this.startY = startY;
        this.addRow(false);
        this.addRow(false);
        this.updateDims();

        this.fastAction = "";
        this.nothingWhenInteraction = true; // нельзы выключать иначе при focus и одновременно resize сохраняется state не тех cell и при Undo row становятся разных размеров
    }

    updateDims(updateRowHeight = true) {
        if (updateRowHeight) {
            for (let i = 0; i < this.rows.length; i++) {
                this.rows[i].updateRowHeight();
            }
        }

        let currY = this.startY;
        for (let i = 0; i < this.rows.length; i++) {
            this.rows[i].updatePosition(this.startX, currY);
            currY += this.rows[i].getHeight();
        }

        let width = 0;
        if (this.rows.length !== 0) {
            width = this.rows[0].getWidth();
        }

        let height = 0;
        for (let i = 0; i < this.rows.length; i++) {
            height += this.rows[i].getHeight();
        }

        this.width = width;
        this.height = height;

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    draw() {
        this.chooseCursor();

        let skipColumn = -1;
        let skipRow = -1;
        if (this.interactionType === "fly") {
            if (this.flyData.mode === "column") {
                skipColumn = this.flyData.num;
            } else {
                skipRow = this.flyData.num;
            }
        }

        for (let i = 0; i < this.rows.length; i++) {
            if (i === skipRow) continue;

            let roundTop = false;
            let roundBottom = false;
            if (i === 0) roundTop = true;
            if (i === this.rows.length - 1) roundBottom = true;

            this.rows[i].draw(roundTop, roundBottom, skipColumn);
        }

        this.drawFly();
        this.drawCellsSelectedOutline();
        this.drawCellsSelecting();

        this.interface?.draw();
    }

    drawCellsSelectedOutline() {
        if (this.interactionType === "") return;

        const selectedCells = this.getSelectedCells();

        for (let i = 0; i < selectedCells.length; i++) {
            const currCell = selectedCells[i];
            drawRoundedRect(currCell.startX, currCell.startY, currCell.width, currCell.height, 0);
            canv.ctx.strokeStyle = "#0B99FF";
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(4);
            canv.ctx.stroke();
        }
    }

    chooseCursor() {
        document.body.style.cursor = "";
        const rowColumnResizeResult = this.isRowColumnResize();
        if (this.interactionType === "rowColumnResize") {
            if (this.rowColumnResizeData?.mode === "column") {
                canv.cursorTypeByEntity = "ew-resize";
            }
            if (this.rowColumnResizeData?.mode === "row") {
                canv.cursorTypeByEntity = "ns-resize";
            }
        } else if (rowColumnResizeResult.status) {
            if (rowColumnResizeResult.mode === "column") {
                canv.cursorTypeByEntity = "ew-resize";
            } else if (rowColumnResizeResult.mode === "row") {
                canv.cursorTypeByEntity = "ns-resize";
            }
        }
    }

    async TryAction(type: string) {
        if (type === "mousedown" && this.isRowColumnResize().status) {
            if (this.interactionType !== "select" && this.interactionType !== "cellFocus") return; // такое может быть только если сделать mouseup где-то над toolBar при selecting или fly
            if (this.interactionType === "cellFocus") {
                this.saveInteraction();
                this.interactionType = "select";
                canv.widgetMenu.reopen();
            }

            this.rowColumnResizeData = this.isRowColumnResize();
            this.fixState("rowColumnResize");
            this.interactionType = "rowColumnResize";
        } else if (this.interactionType === "rowColumnResize") {
            if (type === "mousemove") {
                this.rowColumnResizeApply();
            } else {
                if (this.getSelectedCells().length > 0) {
                    this.saveInteraction();
                    this.interactionType = "select";
                } else {
                    this.stopInteraction();
                    canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                }
            }
        } else if (this.interactionType === "cellFocus") {
            await this.getOnlyCell().TryAction(type);

            if (type === "keyboardDown") {
                this.updateDims();
                this.tryShortcut();
            } else if (type === "mousedown") {
                if (!this.isOver()) {
                    this.stopInteraction();

                    canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                    canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
                } else if (!this.getOnlyCell().isOver()) {
                    const transformedSelection = this.getTransformedSelection();
                    this.stopInteraction();
                    if (canv.keyboard.isShift()) {
                        this.setSelected(
                            transformedSelection.selectionRow,
                            transformedSelection.selectionColumn,
                            transformedSelection.selectionWidth,
                            transformedSelection.selectionHeight,
                            transformedSelection.cellsCount,
                        );
                        this.interactionType = "select";
                        canv.widgetMenu.reopen(); // это нужно при вызове isInteraction изнутри tableWidget
                    } else {
                        this.isInteraction("focus");
                    }
                } else {
                    // клик над выбранной cell
                    this.prepareCellsSelecting(
                        this.selected.selectionRow,
                        this.selected.selectionColumn,
                    );
                }
            } else if (type === "mousemove") {
                if (canv.actionManager.isMouseDown() && !this.getOnlyCell().isOver()) {
                    this.stopInteraction();
                    this.startCellsSelecting();
                }
            }
        } else if (this.interactionType === "select") {
            if (type === "mousedown") {
                if (!this.isOver()) {
                    this.stopInteraction();
                    canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                    canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
                } else {
                    const transformedSelection = this.getTransformedSelection();
                    if (canv.keyboard.isShift()) {
                        this.setSelected(
                            transformedSelection.selectionRow,
                            transformedSelection.selectionColumn,
                            transformedSelection.selectionWidth,
                            transformedSelection.selectionHeight,
                            transformedSelection.cellsCount,
                        );
                        canv.widgetMenu.reopen(); // это нужно при вызове isInteraction изнутри tableWidget
                    } else {
                        this.isInteraction("focus");
                    }
                }
            } else if (type === "keyboardDown") {
                this.tryShortcut();
            }
        } else if (this.interactionType === "fly" || this.interactionType === "fly-prepare") {
            if (this.interactionType === "fly-prepare") {
                // это нужно чтобы спрятать widgetMenu при fly
                canv.widgetMenu.hide();
                this.interactionType = "fly";
            }

            if (type === "mousemove") {
                this.applyFly();
            }
            if (type === "mouseup") {
                this.stopFly();
                this.interactionType = "select";
                canv.widgetMenu.reopen();
            }
        } else if (this.interactionType === "selecting") {
            if (type === "mousemove") {
                this.updateCellsSelection();
            }
            if (type === "mouseup") {
                this.stopCellsSelection();
            }
        }
    }

    isInteraction(context, force = false) {
        if (context === "focus") {
            const res = this.isOverAnyCell();
            if (res.status) {
                this.prepareCellsSelecting(res.row, res.column);
                this.setSelected(res.row, res.column, 1, 1, res.sectionWidth);
                this.startSelectedCellFocus();
                return { type: "interaction" };
            }
        } else if (context === "click") {
            const addBarAction = this.interface?.isAddBarAction();
            const buildAction = this.interface?.isBuildAction();

            if (addBarAction?.status) {
                if (addBarAction.data.type === "column") {
                    this.addColumn();
                } else {
                    this.addRow();
                }
                return { type: "click" };
            } else if (buildAction?.status) {
                const actionData = buildAction.data;
                if (actionData?.type === "adder") {
                    this.rowColumnAddAction(actionData);
                    return { type: "click" };
                } else {
                    if (actionData?.mode === "row") {
                        this.selectRow(actionData.num);
                        this.startFly("row", actionData.num);
                    } else {
                        this.selectColumn(actionData?.columnIndex);
                        this.startFly("column", actionData?.columnIndex);
                    }

                    this.interactionType = "fly-prepare";
                    return { type: "interaction", group: this.parent };
                }
            } else if (this.isRowColumnResize().status) {
                this.rowColumnResizeData = this.isRowColumnResize();
                this.fixState("rowColumnResize");
                this.interactionType = "rowColumnResize";
                return { type: "interaction" };
            }
        }

        return { type: "none" };
    }

    stopInteraction() {
        this.saveInteraction();

        // сбрасываем все
        this.interactionType = "";
        this.clearSelected();
    }

    saveInteraction() {
        if (this.interactionType === "cellFocus") {
            const textPart = this.getOnlyCell().textPart;

            textPart.unfocusText();
            this.aiGenerated = textPart.aiGenerated;

            canv.eventManager.openRecord();
            this.saveState("cellFocus");
            canv.eventManager.closeRecord();
        } else if (this.interactionType === "fly") {
            this.stopFly();
        } else if (this.interactionType === "rowColumnResize") {
            canv.eventManager.openRecord();
            this.saveState("rowColumnResize");
            canv.eventManager.closeRecord();
        }
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            if (this.interactionType === "cellFocus") {
                this.saveInteraction();
                this.interactionType = "select";
                canv.widgetMenu.reopen();
            } else if (this.interactionType === "select") {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
            }
        } else if (canv.keyboard.isEnter()) {
            if (this.interactionType !== "select") return;
            const selectedCells = this.getSelectedCells();
            if (selectedCells.length === 1) {
                this.startSelectedCellFocus();
                selectedCells[0].textPart.selection.selectAll();
            }
        } else if (canv.keyboard.isBackspace() || canv.keyboard.isDelete()) {
            if (this.interactionType !== "select") return;
            this.deleteSelected();
        } else if (canv.keyboard.isSelectAll()) {
            if (this.interactionType !== "select") return;
            this.selectAllCells();
        } else if (canv.keyboard.isCopyCommand()) {
            if (this.interactionType !== "select") return;
            const selectedCells = this.getSelectedCells();
            let text = "";
            for (let i = 0; i < selectedCells.length; i++) {
                text += selectedCells[i].textPart.getText();
                if (i < selectedCells.length - 1) text += " ";
            }

            saveTextToClipboard(text);
        } else if (canv.keyboard.isArrow()) {
            if (this.getSelectedCells().length !== 1) return;
            if (this.interactionType !== "select") return;

            if (canv.keyboard.isLeftArrow()) {
                if (this.selected.selectionColumn !== 0) {
                    this.selected.selectionColumn -= 1;
                    canv.widgetMenu.reopen();
                }
            } else if (canv.keyboard.isRightArrow()) {
                if (this.selected.selectionColumn !== this.rows[0].cells.length - 1) {
                    this.selected.selectionColumn += 1;
                    canv.widgetMenu.reopen();
                }
            } else if (canv.keyboard.isUpArrow()) {
                if (this.selected.selectionRow !== 0) {
                    this.selected.selectionRow -= 1;
                    canv.widgetMenu.reopen();
                }
            } else if (canv.keyboard.isDownArrow()) {
                if (this.selected.selectionRow !== this.rows.length - 1) {
                    this.selected.selectionRow += 1;
                    canv.widgetMenu.reopen();
                }
            }
        }
    }

    startSelectedCellFocus() {
        const onlyCell = this.getOnlyCell();
        this.interactionType = "cellFocus";
        onlyCell.textPart.focusText();
        this.fixState("cellFocus");
        canv.widgetMenu.reopen(); // это нужно при вызове isInteraction изнутри tableWidget
    }

    locateWidgetMenu() {
        if (this.getSelectedCells().length === 0) {
            // такое может быть при rowColumnResize
            return {
                status: false,
            };
        }

        const props = this.getSelectionProps();
        return {
            status: true,
            x: props.startX + props.width / 2,
            y: props.startY,
        };
    }

    getSelectionProps() {
        let minX;
        let maxX;
        let minY;
        let maxY;

        const selectedCells = this.getSelectedCells();

        for (let i = 0; i < selectedCells.length; i++) {
            const cell = selectedCells[i];

            if (minX === undefined || cell.startX < minX) minX = cell.startX;
            if (maxX === undefined || cell.startX + cell.width > maxX)
                maxX = cell.startX + cell.width;

            if (minY === undefined || cell.startY < minY) minY = cell.startY;
            if (maxY === undefined || cell.startY + cell.height > maxY)
                maxY = cell.startY + cell.height;
        }

        return {
            startX: minX,
            startY: minY,
            width: maxX - minX,
            height: maxY - minY,
        };
    }

    getTransformedSelection() {
        const result = {
            selectionColumn: this.selected.selectionColumn,
            selectionRow: this.selected.selectionRow,
            selectionWidth: this.selected.selectionWidth,
            selectionHeight: this.selected.selectionHeight,
            cellsCount: this.selected.cellsCount,
        };

        const overCell = this.isOverAnyCell();

        if (!overCell.status) return result;

        // считаем selectionColumn и selectionWidth
        if (overCell.column >= this.cellsSelectingData.cellColumn) {
            result.selectionWidth = overCell.column - this.cellsSelectingData.cellColumn + 1;
            result.selectionColumn = this.cellsSelectingData.cellColumn;
            result.cellsCount = overCell.column - this.cellsSelectingData.cellColumn + 1;
        } else {
            result.selectionWidth = this.cellsSelectingData.cellColumn - overCell.column + 1;
            result.selectionColumn = overCell.column;
            result.cellsCount = this.cellsSelectingData.cellColumn - overCell.column + 1;
        }
        // считаем selectionRow и selectionHeight
        if (overCell.row >= this.cellsSelectingData.cellRow) {
            result.selectionHeight = overCell.row - this.cellsSelectingData.cellRow + 1;
            result.selectionRow = this.cellsSelectingData.cellRow;
        } else {
            result.selectionHeight = this.cellsSelectingData.cellRow - overCell.row + 1;
            result.selectionRow = overCell.row;
        }

        return result;
    }

    isOverAnyCell(): TableOverAnyCell {
        for (let i = 0; i < this.rows.length; i++) {
            const res = this.rows[i].isOverAnyCell();

            if (res.status) {
                return {
                    status: true,
                    row: i,
                    column: res.column as number,
                    sectionWidth: res.sectionWidth as number,
                };
            }
        }

        return { status: false };
    }

    addRow(shouldEvent = true, position = undefined) {
        if (shouldEvent) this.fixState("rowColumnBuild");

        let columnsAmount = 2;
        let prevRow: TableWidgetRow | null = null;
        let placePosition = this.rows.length;
        if (position !== undefined) {
            placePosition = position;
        }

        if (placePosition - 1 >= 0 && placePosition - 1 <= this.rows.length - 1) {
            columnsAmount = this.rows[placePosition - 1].cells.length;
            prevRow = this.rows[placePosition - 1];
        }

        const newRow = new TableWidgetRow(columnsAmount, this);
        this.rows.splice(placePosition, 0, newRow);
        if (prevRow != null) {
            for (let i = 0; i < newRow.cells.length; i++) {
                newRow.cells[i].width = prevRow.cells[i].width;
                newRow.cells[i].height = prevRow.getHeight();

                newRow.cells[i].copyCellOptions(prevRow.cells[i]);
            }
        }

        if (shouldEvent) {
            canv.eventManager.openRecord();
            this.saveState("rowColumnBuild");
            canv.eventManager.closeRecord();
        }

        this.updateDims();
        canv.widgetMenu.reopen();
    }

    addColumn(shouldEvent = true, position = undefined, positionX = undefined) {
        if (shouldEvent) this.fixState("rowColumnBuild");
        for (let i = 0; i < this.rows.length; i++) {
            let placePosition = this.rows[i].cells.length;
            let isWideCell = false;

            this.rows[i].cells.map((cell, index) => {
                if (cell.startX === positionX) {
                    placePosition = index;
                }
                if (positionX && cell.startX < positionX && cell.startX + cell.width > positionX) {
                    placePosition = index;
                    isWideCell = true;
                }
            });

            if (isWideCell) {
                this.rows[i].cells[placePosition].width += 200;
                this.rows[i].cells[placePosition].sectionWidth += 1;
            } else {
                const newCell = new TableWidgetCell();
                if (placePosition - 1 >= 0 && placePosition - 1 <= this.rows[i].cells.length - 1) {
                    newCell.width = 200;
                    newCell.copyCellOptions(this.rows[i].cells[placePosition - 1]);
                }
                newCell.sectionWidth = 1;

                newCell.height = newCell.textPart.height + 2 * newCell.verticalPadding; // без этого новая ячейка сразу сбрасывает row.isFixedHeight
                this.rows[i].cells.splice(placePosition, 0, newCell);
            }
        }

        if (shouldEvent) {
            canv.eventManager.openRecord();
            this.saveState("rowColumnBuild");
            canv.eventManager.closeRecord();
        }

        this.updateDims();
        canv.widgetMenu.reopen();
    }

    clearSelected() {
        this.setSelected(0, 0, 0, 0, 0);
    }

    getOnlyCell() {
        let correctColumn = 0;
        for (let i = 0; i < this.rows[this.selected.selectionRow].cells.length; i++) {
            const currCell = this.rows[this.selected.selectionRow].cells[i];
            if (correctColumn === this.selected.selectionColumn) {
                return currCell;
            } else {
                correctColumn += currCell.sectionWidth;
            }
        }
        return this.rows[this.selected.selectionRow].cells[this.selected.selectionColumn];
    }

    getSelectedCells(): TableWidgetCell[] {
        const cells: TableWidgetCell[] = [];

        for (
            let i = this.selected.selectionRow;
            i < this.selected.selectionRow + this.selected.selectionHeight && i < this.rows.length;
            i++
        ) {
            let cellsCount = this.selected.cellsCount;
            let columnIndex = 0;

            const columnStart = this.selected.selectionColumn;
            const columnEnd = this.selected.selectionColumn + this.selected.cellsCount;

            for (let k = 0; k < this.rows[i].cells.length; k++) {
                if (columnIndex >= columnEnd) break;

                const currCell = this.rows[i].cells[k];

                if (
                    currCell.sectionWidth === 1 &&
                    columnIndex >= columnStart &&
                    columnIndex <= columnEnd
                ) {
                    cells.push(currCell);
                    cellsCount -= currCell.sectionWidth;
                    columnIndex += currCell.sectionWidth;
                } else if (
                    currCell.sectionWidth > 1 &&
                    (columnIndex === columnStart ||
                        (columnIndex > columnStart && columnIndex < columnEnd) ||
                        (columnIndex + currCell.sectionWidth > columnStart &&
                            columnIndex + currCell.sectionWidth <= columnEnd) ||
                        (columnIndex <= columnStart &&
                            columnIndex + currCell.sectionWidth >= columnEnd))
                ) {
                    cells.push(currCell);
                    cellsCount -= currCell.sectionWidth;
                    columnIndex += currCell.sectionWidth;
                } else {
                    columnIndex += currCell.sectionWidth;
                }
            }
        }

        return cells;
    }

    findCellByUID(uid) {
        for (let i = 0; i < this.rows.length; i++) {
            for (let k = 0; k < this.rows[i].cells.length; k++) {
                if (this.rows[i].cells[k].UID === uid) {
                    return this.rows[i].cells[k];
                }
            }
        }

        return null;
    }

    searchText(text: string) {
        const result: TableSearchTextResult[] = [];

        for (let i = 0; i < this.rows.length; i++) {
            for (let j = 0; j < this.rows[i].cells.length; j++) {
                const cellText = this.rows[i].cells[j].textPart.getText().toLowerCase();

                if (cellText.includes(text)) {
                    result.push({
                        status: true,
                        textWidget: this.rows[i].cells[j].textPart,
                        mode: "text",
                    });
                }
            }
        }

        return result;
    }

    // fly --------------------------------------------------------

    startFly(mode, num) {
        this.fixState("rowColumnFly");
        this.flyData = {
            mode: mode,
            num: num,
        };
    }

    stopFly() {
        this.updateDims();

        canv.eventManager.openRecord();
        this.saveState("rowColumnFly");
        canv.eventManager.closeRecord();

        canv.widgetMenu.reopen();
    }

    applyFly() {
        // в Fly режиме важно не вызывать updateDims, так как он меняет startX, startY ячеек в зависимости от их row, column

        const allowOverflow = 50;
        let cells: TableWidgetCell[] = [];
        let easeXKoeff = 1;
        let easeYKoeff = 1;

        if (this.flyData.mode === "column") {
            if (this.getSelectedCells().find((cell) => cell.isMergedCell())) {
                cells = [];
                return;
            }
            cells = this.getSelectedCells();

            if (cells.length === 0) return;

            const centerX = cells[0].startX + cells[0].width / 2;
            const topY = cells[0].startY;

            // выбираем easeXKoeff
            if (centerX < this.startX) {
                const xOverflowRatio = (this.startX - centerX) / allowOverflow;
                easeXKoeff = 1 - easeInSine(xOverflowRatio);
            } else if (centerX > this.startX + this.width) {
                const xOverflowRatio = (centerX - (this.startX + this.width)) / allowOverflow;
                easeXKoeff = 1 - easeInSine(xOverflowRatio);
            }

            // выбираем easeYKoeff
            if (topY < this.startY) {
                const yOverflowRatio = (this.startY - topY) / allowOverflow;
                easeYKoeff = 1 - easeInSine(yOverflowRatio);
            } else if (topY + this.height > this.startY + this.height) {
                const yOverflowRatio =
                    (topY + this.height - (this.startY + this.height)) / allowOverflow;
                easeYKoeff = 1 - easeInSine(yOverflowRatio);
            }
        } else {
            if (this.flyData.num > this.rows.length - 1) return;

            const row = this.rows[this.flyData.num];

            for (let i = 0; i < row.cells.length; i++) {
                cells.push(row.cells[i]);
            }

            if (cells.length === 0) return;

            const startX = cells[0].startX;
            const centerY = cells[0].startY + cells[0].height / 2;

            // выбираем easeXKoeff
            if (startX < this.startX) {
                const xOverflowRatio = (this.startX - startX) / allowOverflow;
                easeXKoeff = 1 - easeInSine(xOverflowRatio);
            } else if (startX + this.width > this.startX + this.width) {
                const xOverflowRatio =
                    (startX + this.width - (this.startX + this.width)) / allowOverflow;
                easeXKoeff = 1 - easeInSine(xOverflowRatio);
            }

            // выбираем easeYKoeff
            if (centerY < this.startY) {
                const yOverflowRatio = (this.startY - centerY) / allowOverflow;
                easeYKoeff = 1 - easeInSine(yOverflowRatio);
            } else if (centerY > this.startY + this.height) {
                const yOverflowRatio = (centerY - (this.startY + this.height)) / allowOverflow;
                easeYKoeff = 1 - easeInSine(yOverflowRatio);
            }
        }

        const pt = canv.actionManager.getActionPoint();
        const prePt = canv.actionManager.getPreActionPoint();

        //Изменение координат ячеек выбранного ряда/колонки для перемещения в момент перетаскивания
        for (let i = 0; i < cells.length; i++) {
            const clearStartX = cells[i].startX + easeXKoeff * (pt.x - prePt.x);
            const clearStartY = cells[i].startY + easeYKoeff * (pt.y - prePt.y);
            cells[i].updatePosition(clearStartX, clearStartY);
        }

        this.calculateFlyRowColumn();
    }

    drawFly() {
        // fly row, column рисуем отдельно, чтобы они были над остальной таблицей
        if (this.interactionType !== "fly") return;

        if (this.flyData.mode === "column") {
            for (let i = 0; i < this.rows.length; i++) {
                const rowSectionCount = this.rows[i].getSectionCount();

                if (this.flyData.num > rowSectionCount) {
                    continue;
                } // такого быть не должно

                const cell = this.rows[i].getCellByColumnIndex(this.flyData.num);

                const cellRound = { 0: 0, 1: 0, 2: 0, 3: 0 };
                cell.draw(cellRound);
            }
        } else {
            if (this.flyData.num > this.rows.length - 1) return; // такого быть не должно

            this.rows[this.flyData.num].draw(); // не рисуем скругления для летающего блока
        }
    }

    async calculateFlyRowColumn() {
        if (this.flyData.mode === "column") {
            let position = 0;
            const firstRow = this.rows[0];

            const rowSectionCount = firstRow.getSectionCount();

            if (this.flyData.num > rowSectionCount) return; // такого быть не должно

            const firstRowCell = firstRow.getCellByColumnIndex(this.flyData.num);

            const flyCenterX = firstRowCell.startX + firstRowCell.width / 2;

            for (let i = 0; i < firstRow.cells.length; i++) {
                const currCell = firstRow.cells[i];
                if (flyCenterX > currCell.startX + currCell.width / 2) {
                    position += currCell.sectionWidth;
                }
            }

            // определяем куда перемещаем (влево или вправо)
            const isRightShift = position >= this.flyData.num;
            // определяем индекс колонки, валидный для переноса с помощью this.flyData["num"]
            let isValidInsertPosition = true;
            // общее число колонок в ряду
            const columnCount = firstRow.getColumnCount();

            for (let i = 0; i < this.rows.length; i++) {
                if (position === columnCount - 1) break;
                if (!isValidInsertPosition) break;

                let targetColumn = 0;
                let insertIsAccess = false;

                for (let j = 0; j < this.rows[i].cells.length; j++) {
                    if (isRightShift && targetColumn - 1 === position) {
                        insertIsAccess = true;
                        break;
                    }
                    if (!isRightShift && targetColumn === position) {
                        insertIsAccess = true;
                        break;
                    }
                    targetColumn += this.rows[i].cells[j].sectionWidth;
                }

                isValidInsertPosition = insertIsAccess;
            }

            for (let i = 0; i < this.rows.length; i++) {
                const row = this.rows[i];
                const rowSectionCount = row.getSectionCount();

                if (this.flyData.num > rowSectionCount) return; // такого быть не должно

                const correctIndexCell = row.getIndexCellByColumnIndex(this.flyData.num);
                const cell = row.cells[correctIndexCell];

                let correctInsertIndexCell = 0;
                let targetInsertColumn = 0;

                for (let j = 0; j < row.cells.length; j++) {
                    if (position === columnCount - 1) {
                        correctInsertIndexCell = position;
                        break;
                    }
                    if (position === this.flyData.num || !isValidInsertPosition) {
                        correctInsertIndexCell = correctIndexCell;
                        break;
                    }
                    if (position > this.flyData.num && targetInsertColumn - 1 >= position) {
                        correctInsertIndexCell = j - 1;
                        break;
                    }

                    if (position < this.flyData.num && targetInsertColumn >= position) {
                        correctInsertIndexCell = j;
                        break;
                    }
                    targetInsertColumn += row.cells[j].sectionWidth;
                }

                row.cells.splice(correctIndexCell, 1);
                row.cells.splice(correctInsertIndexCell, 0, cell);
            }

            this.flyData.num = isValidInsertPosition ? position : this.flyData.num;
            this.selectColumn(isValidInsertPosition ? position : this.flyData.num, false);
        } else {
            if (this.flyData.num > this.rows.length - 1) return; // такого быть не делжно

            const targetRow = this.rows[this.flyData.num];
            const targetRowFirstCell = targetRow.cells[0];

            let position = 0;

            const flyCenterY = targetRowFirstCell.startY + targetRowFirstCell.height / 2;

            for (let i = 0; i < this.rows.length; i++) {
                const firstCell = this.rows[i].cells[0];

                if (flyCenterY > firstCell.startY + firstCell.height / 2) {
                    position = i + 1;
                }
            }

            if (position > this.flyData.num) {
                //после удаления текущего столбца, позиция вставки уменьшится
                position--;
            }

            this.rows.splice(this.flyData.num, 1);
            this.rows.splice(position, 0, targetRow);

            this.flyData.num = position;
            this.selectRow(position, false);
        }

        // выбираем у каких row,column не будем обновлять startX, startY. Иначе он прыгнут на свои места вместо того, чтобы летать
        let skipColumn = -1;
        let skipRow = -1;
        if (this.flyData.mode === "column") {
            skipColumn = this.flyData.num;
        } else {
            skipRow = this.flyData.num;
        }

        let currY = this.startY;
        for (let i = 0; i < this.rows.length; i++) {
            if (i !== skipRow) {
                if (this.flyData.mode === "column") {
                    skipColumn = this.rows[i].getIndexCellByColumnIndex(this.flyData.num);
                }

                this.rows[i].updatePosition(this.startX, currY, skipColumn);
            }
            currY += this.rows[i].getHeight();
        }
    }

    // cells selecting --------------------------------------------------------

    prepareCellsSelecting(cellRow, cellColumn) {
        const pt = canv.actionManager.getActionPoint();

        this.cellsSelectingData = {
            startX: pt.x,
            startY: pt.y,
            width: 0,
            height: 0,
            simulatedStartX: pt.x,
            simulatedStartY: pt.y,
            simulatedWidth: 0,
            simulatedHeight: 0,

            cellRow: cellRow,
            cellColumn: cellColumn,
        };
    }

    startCellsSelecting() {
        this.interactionType = "selecting";

        const pt = canv.actionManager.getActionPoint();

        this.cellsSelectingData.width = pt.x - this.cellsSelectingData.startX;
        this.cellsSelectingData.height = pt.y - this.cellsSelectingData.startY;

        this.updateCellsSelection();
        canv.widgetMenu.hide();
    }

    stopCellsSelection() {
        this.interactionType = "select";
        canv.widgetMenu.reopen();
    }

    updateCellsSelection() {
        const pt = canv.actionManager.getActionPoint();
        const prePt = canv.actionManager.getPreActionPoint();

        this.cellsSelectingData.width += pt.x - prePt.x;
        this.cellsSelectingData.height += pt.y - prePt.y;

        if (pt.x >= this.cellsSelectingData.startX && pt.y >= this.cellsSelectingData.startY) {
            this.cellsSelectingData.simulatedStartX = this.cellsSelectingData.startX;
            this.cellsSelectingData.simulatedStartY = this.cellsSelectingData.startY;
        } else if (
            pt.x < this.cellsSelectingData.startX &&
            pt.y >= this.cellsSelectingData.startY
        ) {
            this.cellsSelectingData.simulatedStartX =
                this.cellsSelectingData.startX + this.cellsSelectingData.width;
            this.cellsSelectingData.simulatedStartY = this.cellsSelectingData.startY;
        } else if (
            pt.x >= this.cellsSelectingData.startX &&
            pt.y < this.cellsSelectingData.startY
        ) {
            this.cellsSelectingData.simulatedStartX = this.cellsSelectingData.startX;
            this.cellsSelectingData.simulatedStartY =
                this.cellsSelectingData.startY + this.cellsSelectingData.height;
        } else {
            this.cellsSelectingData.simulatedStartX =
                this.cellsSelectingData.startX + this.cellsSelectingData.width;
            this.cellsSelectingData.simulatedStartY =
                this.cellsSelectingData.startY + this.cellsSelectingData.height;
        }

        this.cellsSelectingData.simulatedWidth = Math.abs(this.cellsSelectingData.width);
        this.cellsSelectingData.simulatedHeight = Math.abs(this.cellsSelectingData.height);

        const firstRow = this.rows[0];

        const result = {
            row: -1,
            column: -1,
            width: 0,
            height: 0,
        };

        // выбираем row и height
        for (let i = 0; i < this.rows.length; i++) {
            const firstCell = this.rows[i].cells[0];
            if (result.row === -1) {
                if (
                    this.cellsSelectingData.simulatedStartY <=
                    firstCell.startY + firstCell.height
                ) {
                    result.row = i;
                    result.height = 1;
                }
            } else {
                if (
                    this.cellsSelectingData.simulatedStartY +
                        this.cellsSelectingData.simulatedHeight >=
                    firstCell.startY
                ) {
                    result.height = i - result.row + 1;
                }
            }
        }

        // выбираем column и width
        for (let i = 0; i < this.rows[result.row].cells.length; i++) {
            if (result.column === -1) {
                if (
                    this.cellsSelectingData.simulatedStartX <=
                    this.rows[result.row].cells[i].startX + this.rows[result.row].cells[i].width
                ) {
                    result.column = this.rows[result.row].cells.reduce((acc, curr, index) => {
                        if (index >= i) return acc;
                        return acc + curr.sectionWidth;
                    }, 0);
                    result.width = this.rows[result.row].cells[i].sectionWidth;
                }
            } else {
                if (
                    this.cellsSelectingData.simulatedStartX +
                        this.cellsSelectingData.simulatedWidth >=
                    this.rows[result.row].cells[i].startX
                ) {
                    const columnIndexI = this.rows[result.row].cells.reduce((acc, curr, index) => {
                        if (index >= i) return acc;
                        return acc + curr.sectionWidth;
                    }, 0);
                    result.width = columnIndexI - result.column + 1;
                }
            }
        }

        this.setSelected(result.row, result.column, result.width, result.height, result.width);
    }

    drawCellsSelecting() {
        if (this.interactionType !== "selecting") return;

        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = 2 / canv.m[0];
        canv.ctx.strokeRect(
            this.cellsSelectingData.startX,
            this.cellsSelectingData.startY,
            this.cellsSelectingData.width,
            this.cellsSelectingData.height,
        );

        canv.ctx.fillStyle = "#0B99FF";
        canv.ctx.globalAlpha = 0.2;
        canv.ctx.lineWidth = 2 / canv.m[0];
        canv.ctx.fillRect(
            this.cellsSelectingData.startX,
            this.cellsSelectingData.startY,
            this.cellsSelectingData.width,
            this.cellsSelectingData.height,
        );
        canv.ctx.globalAlpha = 1;
    }

    // Row Column Resize --------------------------------------------------------

    isRowColumnResize(): RowColumnResizeData {
        if (!this.interface?.isInterfaceActive()) {
            return { status: false };
        }

        let targetRow = this.rows[0];

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;
        const allowArea = canv.actionManager.getZoomConstant(2.5);

        for (let i = 0; i < this.rows.length; i++) {
            if (
                y > this.rows[i].cells[0].startY &&
                y < this.rows[i].cells[0].startY + this.rows[i].cells[0].height
            ) {
                targetRow = this.rows[i];
                break;
            }
        }

        // "dir": "invert" - при наведении на левый или верхний край таблицы,
        // во всех остальных случаях "dir": "normal"
        // "mode": "column" - при наведении на левую или правую границу ячейки
        // "mode": "row" - при наведении на верхнюю или нижнюю границу ячейки

        // проверяем column resize
        for (let i = 0; i < targetRow.cells.length; i++) {
            const currCell = targetRow.cells[i];

            if (i === 0) {
                // проверяем левый край. startY и height берем всей таблцы, это не ошибка
                if (
                    x > currCell.startX - allowArea &&
                    x < currCell.startX + allowArea &&
                    y > this.startY &&
                    y < this.startY + this.height
                ) {
                    return {
                        status: true,
                        mode: "column",
                        dir: "invert",
                        num: i,
                    };
                }
            }

            // у последней cell дополнительно проверяем правый край. startY и height берем всей таблцы, это не ошибка
            if (
                x > currCell.startX + currCell.width - allowArea &&
                x < currCell.startX + currCell.width + allowArea &&
                y > this.startY &&
                y < this.startY + this.height
            ) {
                return {
                    status: true,
                    mode: "column",
                    dir: "normal",
                    num: i,
                };
            }
        }

        for (let i = 0; i < this.rows.length; i++) {
            const currRowFirstCell = this.rows[i].cells[0];

            if (i === 0) {
                if (
                    y > currRowFirstCell.startY - allowArea &&
                    y < currRowFirstCell.startY + allowArea &&
                    x > this.startX &&
                    x < this.startX + this.width
                ) {
                    return {
                        status: true,
                        mode: "row",
                        dir: "invert",
                        num: i,
                    };
                }
            }

            if (
                y > currRowFirstCell.startY + currRowFirstCell.height - allowArea &&
                y < currRowFirstCell.startY + currRowFirstCell.height + allowArea &&
                x > this.startX &&
                x < this.startX + this.width
            ) {
                return {
                    status: true,
                    mode: "row",
                    dir: "normal",
                    num: i,
                };
            }
        }

        return { status: false };
    }

    rowColumnResizeApply() {
        const pt = canv.actionManager.getActionPoint();
        pt.x = pt.x * canv.m[0];
        pt.y = pt.y * canv.m[0];

        const prePt = canv.actionManager.getPreActionPoint();

        const x = prePt.x;

        //поправка, величина на которую курсор может находится по правую сторону от границы ячейки
        const allowArea = canv.actionManager.getZoomConstant(2.5);

        const preX = prePt.x * canv.m[0];
        const preY = prePt.y * canv.m[0];

        let translateX = (pt.x - preX) / canv.m[0];
        let translateY = (pt.y - preY) / canv.m[3];

        if (this.rowColumnResizeData?.dir === "invert") {
            translateX *= -1;
            translateY *= -1;
        }

        if (this.rowColumnResizeData?.mode === "column") {
            let diffValue = translateX;
            const findCellByStartX = (startX: number) =>
                this.rowColumnResizeData?.dir === "invert"
                    ? startX - allowArea
                    : startX + allowArea;

            const resizeCells: TableWidgetCell[] = [];
            let haveStopResizeCell = false;

            for (let i = 0; i < this.rows.length; i++) {
                for (let j = 0; j < this.rows[i].cells.length; j++) {
                    if (
                        findCellByStartX(this.rows[i].cells[j].startX) < x &&
                        this.rows[i].cells[j].startX + this.rows[i].cells[j].width + allowArea > x
                    ) {
                        resizeCells.push(this.rows[i].cells[j]);
                        if (
                            diffValue < 0 &&
                            this.rows[i].cells[j].width + diffValue <
                                MIN_CELL_WIDTH * this.rows[i].cells[j].sectionWidth
                        ) {
                            haveStopResizeCell = true;
                        }
                    }
                }
            }

            for (let i = 0; i < resizeCells.length; i++) {
                if (haveStopResizeCell) diffValue = 0;
                resizeCells[i].width += diffValue;
            }

            if (this.rowColumnResizeData.dir === "invert") {
                this.translate(-diffValue, 0);
            }
        } else if (this.rowColumnResizeData?.mode === "row") {
            if ((this.rowColumnResizeData.num as number) <= this.rows.length - 1) {
                const targetRow = this.rows[this.rowColumnResizeData.num as number];

                const memRowHeight = targetRow.getHeight();

                targetRow.updateRowHeight(translateY);

                if (this.rowColumnResizeData.dir === "invert") {
                    this.translate(0, -(targetRow.getHeight() - memRowHeight));
                }
            }
        }

        this.updateDims();
    }

    isRowColumnFullSelected() {
        const result: { status: boolean; mode: string; nums: number[] } = {
            status: false,
            mode: "",
            nums: [],
        }; // mode: row | column

        // для строк
        for (let i = 0; i < this.rows.length; i++) {
            if (this.selected.selectionWidth === this.rows[i].cells.length) {
                if (
                    i >= this.selected.selectionRow &&
                    i < this.selected.selectionRow + this.selected.selectionHeight
                ) {
                    result.status = true;
                    result.mode = "row";
                    result.nums.push(i);
                }
            }
        }

        if (result.status) return result;

        // далее для столбцов

        // let firstRow = this.rows[0];
        //
        // for (let i = 0; i < firstRow.cells.length; i++) {
        //     if (this.selected["selectionHeight"] === this.rows.length) {
        //         if (i >= this.selected["selectionColumn"] && i < this.selected["selectionColumn"] + this.selected["selectionWidth"]) {
        //             result["status"] = true;
        //             result["mode"] = "column";
        //             result["nums"].push(i);
        //         }
        //     }
        // }

        if (
            this.selected.selectionHeight === this.rows.length &&
            this.selected.selectionWidth === 1
        ) {
            result.status = true;
            result.mode = "column";
            result.nums.push(this.selected.selectionColumn);
        }

        return result;
    }

    selectRow(num, reopenWidgetMenu = true) {
        this.prepareCellsSelecting(num, 0);
        const count = this.rows[num].cells.reduce((prev, curr) => prev + curr.sectionWidth, 0);
        this.setSelected(num, 0, this.rows[num].cells.length, 1, count);
        if (reopenWidgetMenu) canv.widgetMenu.reopen();
    }

    selectColumn(num, reopenWidgetMenu = true) {
        this.prepareCellsSelecting(0, num);
        this.setSelected(0, num, 1, this.rows.length, 1);
        if (reopenWidgetMenu) canv.widgetMenu.reopen();
    }

    setSelected(selectionRow, selectionColumn, selectionWidth, selectionHeight, cellsCount) {
        this.selected = {
            selectionRow: selectionRow,
            selectionColumn: selectionColumn,
            selectionWidth: selectionWidth,
            selectionHeight: selectionHeight,
            cellsCount: cellsCount,
        };
    }

    selectAllCells() {
        this.prepareCellsSelecting(0, 0); // чтобы после можно было тыкнуть на другую cell c зажатым shift. Обновляем cellRow, cellColumn
        const count = this.rows[0].cells.reduce((prev, curr) => prev + curr.sectionWidth, 0);
        this.setSelected(0, 0, count, this.rows.length, count);
        canv.widgetMenu.reopen();
    }

    // -------------------------------------------------------------------

    // Delete ------------------------------------------------------

    deleteSelected() {
        if (this.isRowColumnFullSelected().status) {
            const data = this.isRowColumnFullSelected();

            if (data.mode === "row") {
                this.deleteRows(data);
            } else {
                const wildCell = this.getSelectedCells().find((cell) => cell.isMergedCell());
                //если среди есть ячейка шириной в несколько колонок, то запрещаем удаление
                if (wildCell) return;

                this.deleteColumns(data);
            }
        } else {
            canv.eventManager.openRecord();
            this.fixState("cellsClearText");
            const selectedCells = this.getSelectedCells();
            for (let i = 0; i < selectedCells.length; i++) {
                selectedCells[i].clearText();
            }
            this.saveState("cellsClearText");
            canv.eventManager.closeRecord();
        }
    }

    deleteRows(data) {
        if (data.nums.length >= this.rows.length) {
            // нельзя удалять все строки, даже если мы после удаляем сам table widget
            // нельзя отправлять state без строк. Иначе у кого-то может какое-то время быть таблца без строк.
            // И отправлять fixType full если удалились (добавились, поменялись местами) строки или столбцы.
            // Аналогично со столбцами.
            canv.eventManager.openRecord();
            canv.canvasController.deleteProcessor(this.parent);
            selectionManager.clearSelected();
            canv.eventManager.closeRecord();
            return;
        }

        this.fixState("rowColumnBuild");
        let acum = 0;
        for (let i = 0; i < data.nums.length; i++) {
            this.rows.splice(data.nums[i] - acum, 1);
            acum++;
        }

        canv.eventManager.openRecord();
        this.saveState("rowColumnBuild");
        canv.eventManager.closeRecord();

        this.stopInteraction();
        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");

        this.updateDims();
    }

    deleteColumns(data) {
        const firstRow = this.rows[0];

        // на самом деле тут никогда не сработает. Так как в таком случае выбраны и все строки и сработает более приоритетный deleteRows.
        if (data.nums.length >= firstRow.cells.length) {
            canv.eventManager.openRecord();
            canv.canvasController.deleteProcessor(this.parent);
            selectionManager.clearSelected();
            canv.eventManager.closeRecord();
            return;
        }

        this.fixState("rowColumnBuild");
        for (let i = 0; i < this.rows.length; i++) {
            this.rows[i].deleteCellByByColumnIndex(data.nums[0]);
        }

        canv.eventManager.openRecord();
        this.saveState("rowColumnBuild");
        canv.eventManager.closeRecord();

        this.stopInteraction();
        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");

        this.updateDims();
    }

    removeLastColumn() {
        // не кидает event. Используется из tableTool

        for (let i = 0; i < this.rows.length; i++) {
            this.rows[i].cells.splice(this.rows[i].cells.length - 1, 1);
        }

        this.updateDims();
    }

    removeLastRow() {
        // не кидает event. Используется из tableTool

        this.rows.splice(this.rows.length - 1, 1);

        this.updateDims();
    }

    // build actions ------------------------------------------------------

    rowColumnAddAction(data) {
        if (data.mode === "row") {
            if (data.side === "before") {
                this.addRow(true, data.num);
            } else {
                this.addRow(true, data.num + 1);
            }
        } else {
            const positionX = data.drawData.startX + data.drawData.width / 2;
            if (data.side === "before") {
                this.addColumn(true, data.num, positionX);
            } else {
                this.addColumn(true, data.num + 1, positionX);
            }
        }
    }

    // translate resize ---------------------------------------------------

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.updateDims(false);
    }

    resize(x: number, y: number, catchPointer: string) {
        if (catchPointer === "sideRight" || catchPointer === "sideLeft") {
            let maxStartXLastCell = 0;
            let minEndXFirstCell = 0;
            for (let i = 0; i < this.rows.length; i++) {
                if (
                    this.rows[i].cells[this.rows[i].cells.length - 1].startX > maxStartXLastCell ||
                    maxStartXLastCell === 0
                ) {
                    maxStartXLastCell = this.rows[i].cells[this.rows[i].cells.length - 1].startX;
                }

                if (
                    this.rows[i].cells[0].startX + this.rows[i].cells[0].width < minEndXFirstCell ||
                    minEndXFirstCell === 0
                ) {
                    minEndXFirstCell = this.rows[i].cells[0].startX + this.rows[i].cells[0].width;
                }
            }

            for (let i = 0; i < this.rows.length; i++) {
                let columnNum = this.rows[i].cells.length - 1;
                if (catchPointer === "sideLeft") {
                    columnNum = 0;
                }

                if (this.rows[i].cells[columnNum].sectionWidth === 1) {
                    if (this.rows[i].cells[columnNum].width + x < MIN_CELL_WIDTH) {
                        x = MIN_CELL_WIDTH - this.rows[i].cells[columnNum].width;
                    }
                } else {
                    if (
                        this.rows[i].cells[columnNum].width +
                            this.rows[i].cells[columnNum].startX -
                            maxStartXLastCell +
                            x <
                            MIN_CELL_WIDTH &&
                        catchPointer !== "sideLeft"
                    ) {
                        x =
                            MIN_CELL_WIDTH -
                            (this.rows[i].cells[columnNum].width +
                                this.rows[i].cells[columnNum].startX -
                                maxStartXLastCell);
                    }
                    if (
                        minEndXFirstCell - this.rows[i].cells[columnNum].startX + x <
                            MIN_CELL_WIDTH &&
                        catchPointer === "sideLeft"
                    ) {
                        x =
                            MIN_CELL_WIDTH -
                            (minEndXFirstCell - this.rows[i].cells[columnNum].startX);
                    }
                }

                this.rows[i].cells[columnNum].width += x;
            }
        } else if (catchPointer === "sideTop" || catchPointer === "sideBottom") {
            let targetRow = this.rows[0]; // не нужно проверять другие ряды, так как при создании таблицы нет совмещенных ячеек
            if (catchPointer === "sideBottom") {
                targetRow = this.rows[this.rows.length - 1];
            }

            for (let i = 0; i < targetRow.cells.length; i++) {
                targetRow.cells[i].height += y;
            }

            targetRow.updateRowHeight();
        } else {
            const rowWidth = this.rows[0].getWidth();
            const rowColumns = this.rows[0].getSectionCount();

            if (rowWidth > 70 * rowColumns || x > 0) {
                for (let i = 0; i < this.rows.length; i++) {
                    this.rows[i].resize(x, y * (this.rows[i].getHeight() / this.height));
                    this.rows[i].updateRowHeight();
                }
            }
        }

        this.updateDims();
    }

    // Далее обработчики menu ------------------------------------------------

    getSelectedOrAllCells() {
        let cells: TableWidgetCell[] = [];
        const selectedCells = this.getSelectedCells();

        if (selectedCells.length !== 0) {
            // тут нельзя просто смотреть на interactionType, потому что при rowColumnResize может не быть selected
            cells = selectedCells;
        } else {
            for (let i = 0; i < this.rows.length; i++) {
                for (let k = 0; k < this.rows[i].cells.length; k++) {
                    cells.push(this.rows[i].cells[k]);
                }
            }
        }

        return cells;
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];

        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            widgetOptions.push(new widgetOption("color", cells[i].backgroundColor, this.UID));

            if (this.selected.selectionHeight === 1) {
                if (cells.length === 1 && cells[0].isMergedCell()) {
                    widgetOptions.push(new widgetOption("unmerge", cells, this.UID));
                } else if (cells.length > 1) {
                    widgetOptions.push(new widgetOption("merge", cells, this.UID));
                }
            }

            widgetOptions.push(new widgetOption("textSize", cells[i].textPart.fontSize, this.UID));
            widgetOptions.push(new widgetOption("textBold", cells[i].textPart.fontBold, this.UID));
            widgetOptions.push(
                new widgetOption("textStrike", cells[i].textPart.fontStrike, this.UID),
            );
            widgetOptions.push(
                new widgetOption("textBulleted", cells[i].textPart.bullettedList, this.UID),
            );
            widgetOptions.push(new widgetOption("textAlign", cells[i].textPart.align, this.UID));
        }

        if (this.interactionType === "cellFocus") {
            this.getOnlyCell().textPart.setAffectedItemsUniqValues(
                widgetOptions,
                "link",
                "link",
                this.UID,
            );
            this.getOnlyCell().textPart.setEditTextMobile(widgetOptions, this.UID);
        } else if (this.interactionType === "select") {
            const selectedCells = this.getSelectedCells();
            if (selectedCells.length === 1) {
                selectedCells[0].textPart.setAffectedItemsUniqValues(
                    widgetOptions,
                    "link",
                    "link",
                    this.UID,
                );

                selectedCells[0].textPart.setEditTextMobile(widgetOptions, this.UID);
            }
        }

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuBackgroundColor;
        } else if (name === "textSize") {
            return this.menuTextSize;
        } else if (name === "textBold") {
            return this.menuTextBold;
        } else if (name === "textStrike") {
            return this.menuTextStrike;
        } else if (name === "textBulleted") {
            return this.menuTextBulleted;
        } else if (name === "textAlign") {
            return this.menuTextAlign;
        } else if (name === "link") {
            return this.menuLink;
        } else if (name === "merge") {
            return this.menuMergeCells;
        } else if (name === "unmerge") {
            return this.menuUnmergeCells;
        } else if (name === "mobileEdit") {
            return () => this.getOnlyCell().textPart.menuMobileEdit(false);
        }
    }

    // Для menu -----------------------------------------------------------

    menuLink(currentLink: string) {
        if (this.interactionType === "cellFocus") {
            this.getOnlyCell().textPart.menuLink(currentLink, this.updateDims.bind(this));
        } else if (this.interactionType === "select") {
            const selectedCells = this.getSelectedCells();
            if (selectedCells.length === 1) {
                this.startSelectedCellFocus();
                selectedCells[0].textPart.selection.selectAll();
                selectedCells[0].textPart.menuLink(currentLink, this.updateDims.bind(this));
            }
        }
    }

    menuBackgroundColor(color: string) {
        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            cells[i].setBackgroundColor(color);
        }
    }

    menuTextSize(value) {
        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            cells[i].textPart.menuTextSize(value);
        }

        this.updateDims();
    }

    menuTextBulleted(value) {
        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            cells[i].textPart.menuTextBulleted(value);
        }

        this.updateDims();
    }

    menuTextBold(value) {
        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            cells[i].textPart.menuTextBold(value);
        }

        this.updateDims();
    }

    menuTextStrike(value) {
        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            cells[i].textPart.menuTextStrike(value);
        }

        this.updateDims();
    }

    menuTextAlign(value) {
        const cells = this.getSelectedOrAllCells();

        for (let i = 0; i < cells.length; i++) {
            cells[i].textPart.menuTextAlign(value);
            cells[i].alignment = value;
        }

        this.updateDims();
    }

    menuMergeCells() {
        const targetCells = this.getSelectedCells();
        const startX = targetCells[0].startX;
        const startY = targetCells[0].startY;
        let totalWidth = 0;
        let totalSectionWidth = 0;

        let mergedCell: TableWidgetCell | null = null;

        for (let i = 0; i < targetCells.length; i++) {
            totalWidth += targetCells[i].width;
            totalSectionWidth += targetCells[i].sectionWidth;
            if (mergedCell === null && targetCells[i].textPart.lines[0].items.length) {
                mergedCell = targetCells[i];
            }
        }
        if (mergedCell === null) {
            mergedCell = targetCells[0];
        }

        const newCell = new TableWidgetCell();

        newCell.copyCellOptions(mergedCell);
        mergedCell.textPart.lines.forEach((line, i) => {
            const l = new textLine(i, newCell.textPart, line.bullettedList, line.alignment);
            l.items = [...line.items];
            l.enterExists = line.enterExists;
            if (i === 0) {
                newCell.textPart.lines.splice(0, 1, l);
            } else {
                newCell.textPart.lines.push(l);
            }
        });

        newCell.textPart.startX = mergedCell.textPart.startX;
        newCell.textPart.startY = mergedCell.textPart.startY;

        newCell.startX = startX;
        newCell.startY = startY;
        newCell.width = totalWidth;
        newCell.sectionWidth = totalSectionWidth;

        canv.eventManager.openRecord();
        this.fixState("unionCell");

        const row = this.rows[this.selected.selectionRow];
        const cells: TableWidgetCell[] = [];

        row.cells.forEach((cell) => {
            if (cell.UID === mergedCell.UID) {
                cells.push(newCell);
                return;
            }
            if (!targetCells.find((el) => el.UID === cell.UID)) {
                cells.push(cell);
            }
        });

        row.cells = cells;

        this.updateDims();

        this.setSelected(
            this.selected.selectionRow,
            this.selected.selectionColumn,
            mergedCell.sectionWidth,
            this.selected.selectionHeight,
            mergedCell.sectionWidth,
        );

        this.saveState("unionCell");
        canv.eventManager.closeRecord();

        canv.redraw();
        canv.widgetMenu.reopen();
    }

    menuUnmergeCells() {
        const targetCell = this.getOnlyCell();
        const targetCellSectionWidth = targetCell.sectionWidth;
        const targetCellUID = targetCell.UID;
        if (!targetCellSectionWidth || targetCellSectionWidth <= 1) return;

        let cellsCoordinates: TableCoordinates[] = [];

        for (let i = 0; i < this.rows.length; i++) {
            let unmergedCellBL = targetCell.startX;
            const unmergedCellBR = targetCell.startX + targetCell.width;

            const tempCellsCoordinates: TableCoordinates[] = [];
            let pointerCellsCoordinates = 0;

            if (i === this.selected.selectionRow) continue;
            if (this.rows[i].cells.length < targetCell.sectionWidth) continue;

            for (let j = 0; j <= this.rows[i].cells.length; j++) {
                if (unmergedCellBL >= unmergedCellBR) {
                    if (cellsCoordinates.length === 0) {
                        cellsCoordinates = tempCellsCoordinates;
                    } else {
                        const resultCellsCoordinates: TableCoordinates[] = [];
                        for (let n = 0; n < this.rows[i].cells.length; ) {
                            if (
                                !cellsCoordinates[pointerCellsCoordinates] &&
                                tempCellsCoordinates[n]
                            ) {
                                resultCellsCoordinates.push(tempCellsCoordinates[n]);
                                cellsCoordinates = resultCellsCoordinates;
                                break;
                            }
                            if (
                                !cellsCoordinates[pointerCellsCoordinates] &&
                                !tempCellsCoordinates[n]
                            ) {
                                break;
                            }

                            if (
                                cellsCoordinates[pointerCellsCoordinates].RB ===
                                tempCellsCoordinates[n].RB
                            ) {
                                resultCellsCoordinates.push(tempCellsCoordinates[n]);
                                pointerCellsCoordinates++;
                                n++;
                                continue;
                            }
                            if (
                                cellsCoordinates[pointerCellsCoordinates].RB >
                                tempCellsCoordinates[n].RB
                            ) {
                                resultCellsCoordinates.push(tempCellsCoordinates[n]);
                                cellsCoordinates[pointerCellsCoordinates].LB =
                                    tempCellsCoordinates[n].RB;
                                n++;
                                continue;
                            }
                            if (
                                cellsCoordinates[pointerCellsCoordinates].RB <
                                tempCellsCoordinates[n].RB
                            ) {
                                resultCellsCoordinates.push(
                                    cellsCoordinates[pointerCellsCoordinates],
                                );
                                tempCellsCoordinates[n].LB =
                                    cellsCoordinates[pointerCellsCoordinates].RB;
                                pointerCellsCoordinates++;
                            }
                        }
                        cellsCoordinates = resultCellsCoordinates;
                    }
                    break;
                }

                if (j === this.rows[i].cells.length) {
                    if (tempCellsCoordinates.length) {
                        cellsCoordinates = tempCellsCoordinates;
                    }
                    break;
                }

                let currentCellBL = this.rows[i].cells[j].startX;
                let currentCellBR = this.rows[i].cells[j].startX + this.rows[i].cells[j].width;

                if (currentCellBL < unmergedCellBL && currentCellBR <= unmergedCellBL) continue;
                if (currentCellBL >= unmergedCellBR && currentCellBR > unmergedCellBR) continue;

                //корректируем левую границу текущей проверяемой ячейки
                if (currentCellBL < unmergedCellBL) currentCellBL = unmergedCellBL;
                //корректируем правую границу текущей проверяемой ячейки
                if (currentCellBR > unmergedCellBR) currentCellBR = unmergedCellBR;

                //временный массив координат всех ячеек в пределах отрезка
                tempCellsCoordinates.push({
                    LB: unmergedCellBL,
                    RB: currentCellBL <= unmergedCellBL ? currentCellBR : unmergedCellBR,
                });

                unmergedCellBL = currentCellBR;
            }
        }

        let newCells = cellsCoordinates.map((cell, i) => {
            const newCell = new TableWidgetCell();
            newCell.width = cell.RB - cell.LB;
            newCell.startX = cell.LB;
            newCell.startY = targetCell.startY;
            newCell.height = targetCell.height;
            newCell.setBackgroundColor(targetCell.backgroundColor);
            if (i === 0) {
                newCell.copyCellOptions(targetCell);
                targetCell.textPart.lines.forEach((line, i) => {
                    const l = new textLine(i, newCell.textPart, line.bullettedList, line.alignment);
                    l.items = [...line.items];
                    l.enterExists = line.enterExists;
                    if (i === 0) {
                        newCell.textPart.lines.splice(0, 1, l);
                    } else {
                        newCell.textPart.lines.push(l);
                    }
                });
            }
            // устанавливаем дефолтный параметр sectionWidth
            newCell.sectionWidth = 1;

            return newCell;
        });

        if (!newCells.length || newCells.length < targetCellSectionWidth) {
            const separateCells = [...newCells];
            let positionCellIndex = 0;
            let maxCellWidth = 0;
            for (let i = 0; i < targetCellSectionWidth - newCells.length; i++) {
                separateCells.map((cell, index) => {
                    if (maxCellWidth < cell.width) {
                        maxCellWidth = cell.width;
                        positionCellIndex = index;
                    }
                });

                //самая маленькая ширина ячейки 70, если после деления ширина меньше, то прерываем и в текущую ячейку присваиваем остаток не добавленных ячеек
                if (separateCells[positionCellIndex].width / 2 < MIN_CELL_WIDTH) {
                    separateCells[positionCellIndex].sectionWidth =
                        targetCellSectionWidth - newCells.length - i + 1;
                    break;
                }

                const cell = new TableWidgetCell();
                cell.width = separateCells[positionCellIndex].width / 2;
                cell.startX =
                    separateCells[positionCellIndex].startX +
                    separateCells[positionCellIndex].width / 2;
                cell.startY = separateCells[positionCellIndex].startY;
                cell.height = separateCells[positionCellIndex].height;
                cell.copyCellOptions(separateCells[positionCellIndex]);

                separateCells[positionCellIndex].width = separateCells[positionCellIndex].width / 2;

                separateCells.splice(positionCellIndex + 1, 0, cell);

                positionCellIndex = 0;
                maxCellWidth = 0;
            }

            newCells = separateCells;
        }

        const targetRow = this.rows[this.selected.selectionRow];
        const cells: TableWidgetCell[] = [];

        for (let k = 0; k < targetRow.cells.length; k++) {
            if (targetRow.cells[k].UID === targetCellUID) {
                cells.push(...newCells);
            } else {
                cells.push(targetRow.cells[k]);
            }
        }

        canv.eventManager.openRecord();
        this.fixState("separationCell");

        targetRow.cells = cells;

        this.setSelected(
            this.selected.selectionRow,
            this.selected.selectionColumn,
            targetCellSectionWidth,
            this.selected.selectionHeight,
            targetCellSectionWidth,
        );

        this.updateDims();

        this.saveState("separationCell");
        canv.eventManager.closeRecord();

        canv.redraw();
        canv.widgetMenu.reopen();
    }

    // Далее для statesManager ----------------------------------------------

    getRowsStates() {
        const states: TableWidgetRowStateMap["full"][] = [];

        for (let i = 0; i < this.rows.length; i++) {
            states.push(this.rows[i].getState("full"));
        }

        return states;
    }

    loadRowsStates(states) {
        this.rows = [];

        for (let i = 0; i < states.length; i++) {
            const newRow = new TableWidgetRow(0, this);
            newRow.loadState(states[i], "full");
            this.rows.push(newRow);
        }
    }

    getCellsStates(twinState) {
        const states: TableWidgetCellStateMap["props"][] = [];

        if (twinState !== undefined) {
            for (let i = 0; i < twinState.cells.length; i++) {
                const cell = this.findCellByUID(twinState.cells[i].UID);
                if (cell == null) continue; // такого быть не должно
                states.push(cell.getState("props"));
            }
        } else {
            const cells = this.getSelectedOrAllCells();
            for (let i = 0; i < cells.length; i++) {
                states.push(cells[i].getState("props"));
            }
        }

        return states;
    }

    loadCellsStates(states, fixType) {
        for (let i = 0; i < states.length; i++) {
            const cell = this.findCellByUID(getStringProperty("UID", states[i]));
            if (cell != null) {
                // такого быть не должно
                cell.loadState(states[i], fixType);
            }
        }
    }

    resetFlagsAndInteraction() {
        if (this.interactionType === "cellFocus") {
            // выбранной ячейки может уже не быть.
            if (
                this.selected.selectionRow <= this.rows.length - 1 &&
                this.selected.selectionColumn <=
                    this.rows[this.selected.selectionRow].cells.length - 1
            ) {
                this.getOnlyCell().textPart.unfocusText();
            }
        }

        // сбрасываем все
        this.interactionType = "";
        this.clearSelected();

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        if (cursorTool.isMode("interaction") && canv.canvasController.isInSelected(this.parent)) {
            cursorTool.turnMode("");
        }
    }

    checkEvent(stateObj, fixType) {
        if (fixType === "cells") {
            // проверяем наличие всех cells
            for (let i = 0; i < stateObj.cells.length; i++) {
                const cell = this.findCellByUID(stateObj.cells[i].UID);

                if (cell == null) {
                    return false;
                }
            }
        }

        return true;
    }

    getFixType(reason) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "cellFocus":
            case "cellsClearText":
            case "wm_color":
            case "wm_textBold":
            case "wm_textStrike":
            case "wm_textBulleted":
            case "wm_textSize":
            case "wm_textAlign":
                fixType = "cells";
                break;
            case "resize":
                fixType = "full";
                break;
        }

        return fixType;
    }

    getState(fixType, twinState = undefined) {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                };
            case "cells":
                return {
                    cells: this.getCellsStates(twinState),
                    aiGenerated: this.aiGenerated,
                };
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "tableWidget",
                    startX: this.startX,
                    startY: this.startY,
                    rows: this.getRowsStates(),
                    aiGenerated: this.aiGenerated,
                };
        }
    }

    loadState(stateObj, fixType) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "cells":
                this.loadCellsStates(stateObj.cells, "props");
                this.aiGenerated = getBoolProperty("aiGenerated", stateObj);

                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.aiGenerated = getBoolProperty("aiGenerated", stateObj);
                this.loadRowsStates(stateObj.rows);
                this.resetFlagsAndInteraction();
                break;
        }

        this.updateDims();
    }

    constructorName = "table";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
    rows: TableWidgetRow[] = [];

    interface: TableWidgetInterface | null = null;

    selected: TableSelected = {
        selectionRow: 0,
        selectionColumn: 0,
        selectionWidth: 0,
        selectionHeight: 0,
        cellsCount: 0,
    };

    cellsSelectingData: TableCellsSelectingData = {
        startX: 0,
        startY: 0,
        width: 0,
        height: 0,
        simulatedStartX: 0,
        simulatedStartY: 0,
        simulatedWidth: 0,
        simulatedHeight: 0,
        cellRow: 0,
        cellColumn: 0,
    };

    flyData = {
        mode: "",
        num: 0,
    };

    interactionType = "";
    aiGenerated = false;
    rowColumnResizeData: RowColumnResizeData | null = null;
}
